import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const DocumentTable = ({ 
  documents, 
  selectedDocuments, 
  onSelectDocument, 
  onSelectAll, 
  onPreviewDocument, 
  onViewVersions, 
  onRequestSignature 
}) => {
  const [sortField, setSortField] = useState('uploadDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedDocuments = [...documents]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (sortField === 'uploadDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-warning text-warning-foreground', icon: 'Clock' },
      'signed': { color: 'bg-success text-success-foreground', icon: 'CheckCircle' },
      'rejected': { color: 'bg-error text-error-foreground', icon: 'XCircle' },
      'expired': { color: 'bg-muted text-muted-foreground', icon: 'AlertTriangle' }
    };
    
    const config = statusConfig?.[status] || statusConfig?.['pending'];
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const getDocumentTypeIcon = (type) => {
    const typeIcons = {
      'deed': 'FileText',
      'contract': 'FileSignature',
      'assessment': 'ClipboardCheck',
      'certificate': 'Award',
      'report': 'BarChart3',
      'other': 'File'
    };
    return typeIcons?.[type] || 'File';
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedDocuments?.length === documents?.length && documents?.length > 0}
                  indeterminate={selectedDocuments?.length > 0 && selectedDocuments?.length < documents?.length}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted/70 transition-smooth"
                onClick={() => handleSort('filename')}
              >
                <div className="flex items-center space-x-2">
                  <span>Document</span>
                  {sortField === 'filename' && (
                    <Icon 
                      name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted/70 transition-smooth"
                onClick={() => handleSort('transactionId')}
              >
                <div className="flex items-center space-x-2">
                  <span>Transaction</span>
                  {sortField === 'transactionId' && (
                    <Icon 
                      name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted/70 transition-smooth"
                onClick={() => handleSort('type')}
              >
                <div className="flex items-center space-x-2">
                  <span>Type</span>
                  {sortField === 'type' && (
                    <Icon 
                      name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="text-left px-4 py-3 font-medium text-foreground cursor-pointer hover:bg-muted/70 transition-smooth"
                onClick={() => handleSort('uploadDate')}
              >
                <div className="flex items-center space-x-2">
                  <span>Upload Date</span>
                  {sortField === 'uploadDate' && (
                    <Icon 
                      name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  )}
                </div>
              </th>
              <th className="text-left px-4 py-3 font-medium text-foreground">Status</th>
              <th className="text-left px-4 py-3 font-medium text-foreground">Blockchain</th>
              <th className="text-right px-4 py-3 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedDocuments?.map((document) => (
              <tr key={document?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedDocuments?.includes(document?.id)}
                    onChange={(e) => onSelectDocument(document?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={getDocumentTypeIcon(document?.type)} size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{document?.filename}</p>
                      <p className="text-sm text-muted-foreground">{document?.size}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div>
                    <p className="font-medium text-foreground">#{document?.transactionId}</p>
                    <p className="text-sm text-muted-foreground">{document?.propertyAddress}</p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="capitalize text-foreground">{document?.type}</span>
                </td>
                <td className="px-4 py-4">
                  <div>
                    <p className="text-foreground">{new Date(document.uploadDate)?.toLocaleDateString()}</p>
                    <p className="text-sm text-muted-foreground">{document?.uploadedBy}</p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(document?.signatureStatus)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    {document?.blockchainVerified ? (
                      <>
                        <Icon name="Shield" size={16} className="text-success" />
                        <span className="text-sm text-success font-medium">Verified</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Clock" size={16} className="text-warning" />
                        <span className="text-sm text-warning font-medium">Pending</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onPreviewDocument(document)}
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewVersions(document)}
                    >
                      <Icon name="History" size={16} />
                    </Button>
                    {document?.signatureStatus === 'pending' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onRequestSignature(document)}
                      >
                        <Icon name="PenTool" size={16} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4 p-4">
        {sortedDocuments?.map((document) => (
          <div key={document?.id} className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 flex-1">
                <Checkbox
                  checked={selectedDocuments?.includes(document?.id)}
                  onChange={(e) => onSelectDocument(document?.id, e?.target?.checked)}
                />
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={getDocumentTypeIcon(document?.type)} size={18} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{document?.filename}</p>
                  <p className="text-sm text-muted-foreground">{document?.size}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onPreviewDocument(document)}
                >
                  <Icon name="Eye" size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onViewVersions(document)}
                >
                  <Icon name="History" size={16} />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Transaction</p>
                <p className="font-medium text-foreground">#{document?.transactionId}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Type</p>
                <p className="font-medium text-foreground capitalize">{document?.type}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Upload Date</p>
                <p className="font-medium text-foreground">{new Date(document.uploadDate)?.toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                {getStatusBadge(document?.signatureStatus)}
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div className="flex items-center space-x-2">
                {document?.blockchainVerified ? (
                  <>
                    <Icon name="Shield" size={14} className="text-success" />
                    <span className="text-sm text-success font-medium">Blockchain Verified</span>
                  </>
                ) : (
                  <>
                    <Icon name="Clock" size={14} className="text-warning" />
                    <span className="text-sm text-warning font-medium">Verification Pending</span>
                  </>
                )}
              </div>
              {document?.signatureStatus === 'pending' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRequestSignature(document)}
                  iconName="PenTool"
                  iconPosition="left"
                >
                  Sign
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentTable;